// lib/theme.ts - Consolidated theme system
import chroma from 'chroma-js';

// ============================================================================
// Type Definitions
// ============================================================================

export interface ThemeColors {
  primary?: string;
  primaryHover?: string;
  primaryActive?: string;
  secondary?: string;
  secondaryHover?: string;
  secondaryActive?: string;
  success?: string;
  successHover?: string;
  warning?: string;
  warningHover?: string;
  error?: string;
  errorHover?: string;
  info?: string;
  infoHover?: string;
  background?: string;
  backgroundSecondary?: string;
  foreground?: string;
  surface?: string;
  surfaceHover?: string;
  surfaceSecondary?: string;
  text?: string;
  textSecondary?: string;
  textMuted?: string;
  textDisabled?: string;
  border?: string;
  borderLight?: string;
  borderDark?: string;
  // Component-specific colors
  headerBg?: string;
  headerText?: string;
  footerBg?: string;
  footerText?: string;
  sidebarBg?: string;
  sidebarText?: string;
  [key: string]: string | undefined;
}

export interface ThemeTypography {
  fontFamily?: {
    primary?: string;
    secondary?: string;
    mono?: string;
  };
  fontSize?: Record<string, string>;
  fontWeight?: Record<string, number>;
  lineHeight?: Record<string, number>;
}

export interface ThemeSpacing {
  xs?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  '2xl'?: string;
  '3xl'?: string;
  // Component-specific spacing
  header?: string;
  footer?: string;
  section?: string;
  card?: string;
  [key: string]: string | undefined;
}

export interface ThemeConfig {
  name: string;
  mode: 'light' | 'dark';
  colors?: ThemeColors;
  typography?: ThemeTypography;
  spacing?: ThemeSpacing;
  borderRadius?: Record<string, string>;
  shadows?: Record<string, string>;
  transitions?: Record<string, string>;
  zIndex?: Record<string, number>;
  breakpoints?: Record<string, string>;
}

export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950?: string;
}

// ============================================================================
// Color Scale Generation
// ============================================================================

/**
 * Generate a full color scale (50-900) from a base color
 * Uses chroma-js to create perceptually uniform scales
 */
export function generateColorScale(baseColor: string, mode: 'light' | 'dark' = 'light'): ColorScale {
  try {
    const color = chroma(baseColor);

    if (mode === 'light') {
      // For light mode: 50 is lightest, 900 is darkest
      // 500 is the base color
      const scale = chroma
        .scale([
          color.brighten(3).saturate(-0.5), // 50 - very light
          color.brighten(2.5).saturate(-0.3), // 100
          color.brighten(2), // 200
          color.brighten(1.5), // 300
          color.brighten(0.5), // 400
          color, // 500 - base
          color.darken(0.5), // 600
          color.darken(1), // 700
          color.darken(1.5), // 800
          color.darken(2).saturate(0.3), // 900 - very dark
        ])
        .mode('lch')
        .colors(10);

      return {
        50: scale[0],
        100: scale[1],
        200: scale[2],
        300: scale[3],
        400: scale[4],
        500: scale[5],
        600: scale[6],
        700: scale[7],
        800: scale[8],
        900: scale[9],
        950: chroma(scale[9]).darken(0.5).hex(),
      };
    } else {
      // For dark mode: invert the scale logic
      const scale = chroma
        .scale([
          color.darken(2).saturate(0.3), // 50 - darkest
          color.darken(1.5),
          color.darken(1),
          color.darken(0.5),
          color,
          color.brighten(0.5),
          color.brighten(1),
          color.brighten(1.5),
          color.brighten(2),
          color.brighten(2.5).saturate(-0.3), // 900 - lightest
        ])
        .mode('lch')
        .colors(10);

      return {
        50: scale[0],
        100: scale[1],
        200: scale[2],
        300: scale[3],
        400: scale[4],
        500: scale[5],
        600: scale[6],
        700: scale[7],
        800: scale[8],
        900: scale[9],
        950: chroma(scale[0]).darken(0.5).hex(),
      };
    }
  } catch (error) {
    console.error('Failed to generate color scale:', error);
    // Return a grayscale fallback
    return {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
    };
  }
}

/**
 * Generate hover and active variants for a color
 */
export function generateInteractiveVariants(baseColor: string): {
  hover: string;
  active: string;
} {
  try {
    const color = chroma(baseColor);
    return {
      hover: color.brighten(0.3).hex(),
      active: color.darken(0.3).hex(),
    };
  } catch (error) {
    console.error('Failed to generate interactive variants:', error);
    return {
      hover: baseColor,
      active: baseColor,
    };
  }
}

// ============================================================================
// Theme Application
// ============================================================================

/**
 * Apply theme configuration to CSS custom properties
 * Automatically generates color scales for main theme colors
 */
export function applyTheme(theme: ThemeConfig): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  // Apply colors with automatic scale generation
  if (theme.colors) {
    const mainColors = ['primary', 'secondary', 'success', 'error', 'warning', 'info'];

    Object.entries(theme.colors).forEach(([key, value]) => {
      if (!value) return;

      const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);

      // Generate full color scale for main colors
      if (mainColors.includes(key) && !key.includes('Hover') && !key.includes('Active')) {
        const scale = generateColorScale(value, theme.mode);
        Object.entries(scale).forEach(([shade, color]) => {
          root.style.setProperty(`${cssVarName}-${shade}`, color);
        });

        // Generate interactive variants if not provided
        if (!theme?.colors?.[`${key}Hover` as keyof ThemeColors]) {
          const variants = generateInteractiveVariants(value);
          root.style.setProperty(`${cssVarName}-hover`, variants.hover);
          root.style.setProperty(`${cssVarName}-active`, variants.active);
        }
      }
    });
  }

  // Apply typography
  if (theme.typography) {
    if (theme.typography.fontFamily) {
      Object.entries(theme.typography.fontFamily).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--font-family-${key}`, value);
        }
      });
    }

    if (theme.typography.fontSize) {
      Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--font-size-${key}`, value);
        }
      });
    }

    if (theme.typography.fontWeight) {
      Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
        root.style.setProperty(`--font-weight-${key}`, value.toString());
      });
    }

    if (theme.typography.lineHeight) {
      Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
        root.style.setProperty(`--line-height-${key}`, value.toString());
      });
    }
  }

  // Apply spacing
  if (theme.spacing) {
    Object.entries(theme.spacing).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--spacing-${key}`, value);
        // Also set theme-prefixed version for Tailwind
        root.style.setProperty(`--spacing-theme-${key}`, value);
      }
    });
  }

  // Apply border radius
  if (theme.borderRadius) {
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--radius-${key}`, value);
        root.style.setProperty(`--radius-theme-${key}`, value);
      }
    });
  }

  // Apply shadows
  if (theme.shadows) {
    Object.entries(theme.shadows).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--shadow-${key}`, value);
        root.style.setProperty(`--shadow-theme-${key}`, value);
      }
    });
  }

  // Apply transitions
  if (theme.transitions) {
    Object.entries(theme.transitions).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--transition-${key}`, value);
        root.style.setProperty(`--transition-theme-${key}`, value);
      }
    });
  }

  // Apply z-index
  if (theme.zIndex) {
    Object.entries(theme.zIndex).forEach(([key, value]) => {
      root.style.setProperty(`--z-index-${key}`, value.toString());
      root.style.setProperty(`--z-index-theme-${key}`, value.toString());
    });
  }

  // Set mode attribute for CSS selectors
  root.setAttribute('data-theme', theme.name);
  root.setAttribute('data-theme-mode', theme.mode);

  // Set class for Tailwind dark mode if applicable
  if (theme.mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// ============================================================================
// Theme Loading
// ============================================================================

/**
 * Load theme from JSON file
 */
export async function loadTheme(themePath: string): Promise<ThemeConfig> {
  const response = await fetch(themePath);
  if (!response.ok) {
    throw new Error(`Failed to load theme: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Load theme by name and mode
 */
export async function loadThemeByName(themeName: string, mode: 'light' | 'dark' = 'light'): Promise<ThemeConfig> {
  return loadTheme(`/themes/${themeName}/${mode}.json`);
}

/**
 * Initialize theme on app load
 */
export async function initializeTheme(themePath?: string, themeName?: string, mode?: 'light' | 'dark'): Promise<ThemeConfig> {
  try {
    let theme: ThemeConfig;

    if (themePath) {
      theme = await loadTheme(themePath);
    } else if (themeName) {
      theme = await loadThemeByName(themeName, mode);
    } else {
      // Default fallback
      theme = await loadTheme('/themes/light.json');
    }

    applyTheme(theme);
    return theme;
  } catch (error) {
    console.error('Failed to initialize theme:', error);
    throw error;
  }
}

// ============================================================================
// Theme Utilities
// ============================================================================

/**
 * Get current theme from CSS variables
 */
export function getCurrentTheme(): Partial<ThemeConfig> {
  if (typeof document === 'undefined') return {};

  const root = document.documentElement;
  const style = getComputedStyle(root);

  return {
    name: root.getAttribute('data-theme') || 'unknown',
    mode: (root.getAttribute('data-theme-mode') as 'light' | 'dark') || 'light',
    colors: {
      primary: style.getPropertyValue('--color-primary'),
      secondary: style.getPropertyValue('--color-secondary'),
      success: style.getPropertyValue('--color-success'),
      error: style.getPropertyValue('--color-error'),
      warning: style.getPropertyValue('--color-warning'),
      info: style.getPropertyValue('--color-info'),
    },
  };
}

/**
 * Switch between light and dark mode for current theme
 */
export async function toggleThemeMode(currentTheme: ThemeConfig): Promise<ThemeConfig> {
  const newMode = currentTheme.mode === 'light' ? 'dark' : 'light';
  return loadThemeByName(currentTheme.name, newMode);
}

/**
 * Preload theme for faster switching
 */
export async function preloadTheme(themeName: string, mode: 'light' | 'dark'): Promise<void> {
  try {
    await loadThemeByName(themeName, mode);
  } catch (error) {
    //console.warn(`Failed to preload theme ${themeName}/${mode}:`, error);
  }
}

// ============================================================================
// Exports
// ============================================================================

export default {
  applyTheme,
  loadTheme,
  loadThemeByName,
  initializeTheme,
  getCurrentTheme,
  toggleThemeMode,
  preloadTheme,
  generateColorScale,
  generateInteractiveVariants,
};
